import { Box } from '@withjoy/joykit';
import React, { useEffect, useState } from 'react';
import { ProductDetails } from '../../components/ProductDetails';
import { useCatalogProductDetails } from './CatalogProductDetails.controller';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { StaticRouterProps } from '@shared/core';
import { useTranslation } from '@shared/core';
import { Helmet } from 'react-helmet-async';
import {
  useCatalogProductDetailsHealth,
  wrapWithCatalogProductDetailsHealthProvider,
} from '@apps/registry/common/components/Catalog/routes/CatalogProductDetails/CatalogProductDetails.health';
import { Seo } from '@shared/components/Seo';
import { getMetaDescription } from '../../utils/metaDescription';
import PageNotFound from '@shared/components/PageNotFound/PageNotFound';
import { useRegistryShopping } from '@apps/registry/admin/RegistryShopping.provider';
import { getShopStickyTopInsetStyle } from '../../Catalog.constants';
import type { SwatchHover } from './CatalogProductDetails.types';
import { resolveHoverPreviewProduct } from './CatalogProductDetails.utils';

export const CatalogProductDetails = wrapWithCatalogProductDetailsHealthProvider((props: { catalogItemId: string; staticContext?: StaticRouterProps['context'] }) => {
  const {
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    hasError,
    error,
    product,
    prefetchedProduct,
    productLoading,
    getCatalogProductDetails,
    isProductInRegistry,
    isAdminView,
  } = useCatalogProductDetails(props);
  const [swatchHover, setSwatchHover] = useState<SwatchHover | null>(null);
  useEffect(() => {
    setSwatchHover(null);
  }, [props.catalogItemId]);

  const isMobileOrTablet = useIsMobileOrTablet();

  const { t } = useTranslation('curationsRegistry');
  const { isEnabled: isShoppingEnabled } = useRegistryShopping();

  const curationsTranslations = t('curations');
  const defaultDepartmentTitle = curationsTranslations.defaultDepartmentTitle();

  const { onLoadComplete, onLoadFailure } = useCatalogProductDetailsHealth();
  useEffect(() => {
    if (error) {
      onLoadFailure('GetCatalogProductDetailsQuery failed', error);
    } else if (product) {
      onLoadComplete();
    }
  }, [error, onLoadFailure, product, onLoadComplete]);

  const handleHover = (pid?: string, isHoverExit: boolean = false) => {
    if (isHoverExit || !pid || pid === props.catalogItemId) {
      setSwatchHover(null);
      return;
    }
    setSwatchHover({ itemId: pid, fromCatalogItemId: props.catalogItemId });
    getCatalogProductDetails({
      variables: {
        catalogItemId: pid,
        withPdpSections: true,
        withProductRating: true,
        withInventory: isShoppingEnabled,
      },
    });
  };

  const hoverPreviewProduct = resolveHoverPreviewProduct({ hover: swatchHover, catalogItemId: props.catalogItemId, prefetchedProduct });

  const metaDescription = getMetaDescription(
    product?.shortDescription?.trim() ||
      product?.longDescription?.trim() ||
      product?.pdpSections?.find(section => section.sectionType === 'details')?.sectionTree?.elements[2].text,
    product?.title,
    product?.brand?.name,
  );

  if (!product && !productLoading) {
    if (props.staticContext) {
      props.staticContext.statusCode = 404;
    }
    return <PageNotFound />;
  }

  return (
    <Box
      paddingX={['24px', null, null, null, null, '88px']}
      paddingBottom={`${180 / 16}rem`}
      paddingTop={isMobileOrTablet ? 8 : 0}
      margin="auto"
      marginTop={[0, null, 5]}
      maxWidth={'1568px'}
      style={getShopStickyTopInsetStyle(isAdminView) as React.CSSProperties}
    >
      {product && (
        <>
          <Seo
            title={`${product.brand?.name} ${product.title} | Joy`}
            description={metaDescription}
          />
          <Helmet>
            {product.title && (
              <meta
                property="og:title"
                content={product.title}
              />
            )}
            {!!product.photos[0]?.url && (
              <meta
                property="og:image"
                content={product.photos[0].url}
              />
            )}
          </Helmet>
        </>
      )}
      <ProductDetails
        key={product?.id}
        showSkeleton={!product || !!hasError}
        product={product}
        addCatalogItem={addCatalogItemToRegistry}
        addCatalogItemLoading={addCatalogItemLoading}
        defaultDepartmentTitle={defaultDepartmentTitle}
        isDialog={false}
        showFooter={true}
        handleHover={handleHover}
        hoverPreviewProduct={hoverPreviewProduct}
        isItemAddedToRegistry={isProductInRegistry(props.catalogItemId)}
      />
    </Box>
  );
});

CatalogProductDetails.displayName = 'CatalogProductDetails';
